import React, { useCallback, useState } from 'react';
import { Trophy, Target, Ticket } from 'lucide-react';
import NotificationsPanel from '../features/notifications/NotificationsPanel';
import InvitationsPanel from '../features/invitations/InvitationsPanel';
import MyWeek from '../features/week/MyWeek';
import HousePanel from '../features/house/HousePanel';
import LandingPage from '../features/landing/LandingPage';
import { GAME_KINDS } from '../features/games/gameKinds';

/**
 * Two pages behind one route, and that is the point of the split.
 *
 * A stranger and a player who is mid-season want opposite things from this URL. The one
 * page that tried to serve both showed the returning player a hero reading "Get Started"
 * over four live games, and showed the stranger a grid of buttons that all went to the
 * register form. Neither is a layout problem - they are two pages.
 *
 * Signed out is {@link LandingPage}. Signed in is below, and its rule is: **Home is
 * today, My Games is everything I am in.** Home never lists all your games. The moment
 * it does it is a second launcher and there is no reason to have two.
 */
function HomePage({ isLoggedIn, onNavigate, onAction, onOpenGame, onOpenLeague, onOpenHouseBoard }) {
  /**
   * The game already leading the page as a deadline, if there is one.
   *
   * Reported by NotificationsPanel rather than worked out here: it is the server's
   * ranking that chose the row, and the week strip below needs to leave that game out
   * rather than say the same thing again at a quieter weight.
   */
  const [promotedGameId, setPromotedGameId] = useState(null);
  const handlePromoted = useCallback((gameId) => setPromotedGameId(gameId), []);

  if (!isLoggedIn) {
    return (
      <div className="page-container fade-in">
        <LandingPage onNavigate={onNavigate} onAction={onAction} />
      </div>
    );
  }

  return (
    <div className="page-container fade-in">
      <div className="signed-in-home">
        {/* What is on you, most urgent first. Renders nothing at all when nothing is
            waiting - see NotificationsPanel, which also promotes the soonest pick out
            of the list and into the top of the page. */}
        <NotificationsPanel
          onOpenGame={onOpenGame}
          onOpenLeague={onOpenLeague}
          onPromoted={handlePromoted}
        />

        {/* Below it, because an invitation is somebody else's timetable and a pick
            deadline is yours. Unlike everything above, these are answered here: you are
            not in the league yet, so there is no page to send you to. */}
        <InvitationsPanel onOpenLeague={onOpenLeague} onOpenGame={onOpenGame} />

        {/* Status rather than a to-do list, which is why it sits under both of those
            and not among them. */}
        <MyWeek excludeGameId={promotedGameId} onOpenGame={onOpenGame} />

        {/* And global news last of all. It is the only thing here that is not about
            you, and on a day with nothing waiting it becomes the top of the page -
            which is the case that makes the board worth having at all. */}
        <HousePanel onOpenBoard={onOpenHouseBoard} />

        <StartSomething onAction={onAction} />
      </div>
    </div>
  );
}

const STARTERS = [
  {
    id: 'join-game',
    icon: Ticket,
    title: 'Join with a code',
    blurb: 'Game or league — it works out which.',
  },
  {
    id: 'create-game',
    icon: Target,
    title: 'Start a one-off game',
    blurb: GAME_KINDS.standalone.short,
  },
  {
    id: 'create-league',
    icon: Trophy,
    title: 'Start a mini league',
    blurb: GAME_KINDS.league.short,
  },
];

/**
 * The three ways in, kept at the foot rather than made the centrepiece.
 *
 * It is the whole page for somebody who signed up an hour ago and is in nothing yet, and
 * it is a quiet footer for everyone else - which is the right weight for both without
 * this page having to ask the server how many games anybody has. The alternative, a
 * first-run block gated on an empty games list, buys a slightly tidier page for an
 * established player at the price of a request on every single load.
 */
function StartSomething({ onAction }) {
  return (
    <section className="start-something">
      <h2 className="section-eyebrow">Start something</h2>
      <div className="start-list">
        {STARTERS.map(({ id, icon: Icon, title, blurb }) => (
          <button key={id} className="start-card" onClick={() => onAction(id)}>
            <span className="start-icon"><Icon size={22} /></span>
            <span className="start-text">
              <span className="start-title">{title}</span>
              <span className="start-blurb">{blurb}</span>
            </span>
          </button>
        ))}
      </div>
    </section>
  );
}

export default HomePage;
